import React, { forwardRef } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@utils/cn'

// Types
import type { HTMLAttributes } from 'react'

const cardVariants = cva(
  'rounded-xl border bg-white text-neutral-900 shadow-card overflow-hidden',
  {
    variants: {
      variant: {
        default: 'border-neutral-200',
        outlined: 'border-neutral-300 shadow-none',
        elevated: 'border-neutral-200 shadow-feature',
        ghost: 'border-transparent shadow-none bg-transparent'
      },
      padding: {
        none: 'p-0',
        sm: 'p-3',
        default: 'p-4',
        lg: 'p-6',
        xl: 'p-8'
      },
      hover: {
        none: '',
        lift: 'transition-all duration-300 hover:shadow-feature hover:-translate-y-1',
        scale: 'transition-transform duration-200 hover:scale-105',
        glow: 'transition-shadow duration-300 hover:shadow-xl hover:shadow-primary-500/10'
      }
    },
    defaultVariants: {
      variant: 'default',
      padding: 'default',
      hover: 'none'
    }
  }
)

export interface CardProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  asChild?: boolean
}

const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, padding, hover, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardVariants({ variant, padding, hover, className }))}
      {...props}
    />
  )
)

Card.displayName = 'Card'

// Card Header Component
export interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  divided?: boolean
}

const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, divided = false, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex flex-col space-y-1.5 p-6',
        divided && 'border-b border-neutral-200',
        className
      )}
      {...props}
    />
  )
)

CardHeader.displayName = 'CardHeader'

// Card Title Component
export interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

const CardTitle = forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, as: Comp = 'h3', ...props }, ref) => (
    <Comp
      ref={ref}
      className={cn('text-lg font-semibold leading-none tracking-tight', className)}
      {...props}
    />
  )
)

CardTitle.displayName = 'CardTitle'

// Card Description Component
const CardDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-neutral-600', className)}
    {...props}
  />
))

CardDescription.displayName = 'CardDescription'

// Card Content Component
const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('p-6 pt-0', className)}
      {...props}
    />
  )
)

CardContent.displayName = 'CardContent'

// Card Footer Component
export interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  divided?: boolean
  centered?: boolean
}

const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, divided = false, centered = false, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex items-center p-6 pt-0',
        divided && 'border-t border-neutral-200 pt-6',
        centered && 'justify-center',
        className
      )}
      {...props}
    />
  )
)

CardFooter.displayName = 'CardFooter'

// Card Image Component
export interface CardImageProps extends HTMLAttributes<HTMLDivElement> {
  src: string
  alt: string
  aspectRatio?: 'square' | 'video' | 'auto'
  objectFit?: 'cover' | 'contain' | 'fill'
}

const CardImage = forwardRef<HTMLDivElement, CardImageProps>(
  ({
    className,
    src,
    alt,
    aspectRatio = 'auto',
    objectFit = 'cover',
    ...props
  }, ref) => (
    <div
      ref={ref}
      className={cn(
        'overflow-hidden',
        aspectRatio === 'square' && 'aspect-square',
        aspectRatio === 'video' && 'aspect-video',
        className
      )}
      {...props}
    >
      <img
        src={src}
        alt={alt}
        className={cn(
          'h-full w-full transition-transform duration-300 hover:scale-105',
          objectFit === 'cover' && 'object-cover',
          objectFit === 'contain' && 'object-contain',
          objectFit === 'fill' && 'object-fill'
        )}
      />
    </div>
  )
)

CardImage.displayName = 'CardImage'

// Company Card - Specific component for company listings
export interface CompanyCardProps extends Omit<CardProps, 'children'> {
  company: {
    id: string
    name: string
    description?: string
    logo?: string
    location?: string
    services?: string[]
    phone?: string
    website?: string
    featured?: boolean
  }
  onContact?: (type: 'phone' | 'website', value: string) => void
  showServices?: boolean
  showDescription?: boolean
}

const CompanyCard = forwardRef<HTMLDivElement, CompanyCardProps>(
  ({
    company,
    onContact,
    showServices = true,
    showDescription = true,
    className,
    ...props
  }, ref) => (
    <Card
      ref={ref}
      hover="lift"
      className={cn('company-card', className)}
      {...props}
    >
      <div className="flex items-start space-x-4 p-6">
        {/* Company Logo */}
        <div className="flex-shrink-0">
          {company.logo ? (
            <img
              src={company.logo}
              alt={`${company.name} โลโก้`}
              className="company-logo"
            />
          ) : (
            <div className="company-logo flex items-center justify-center bg-primary-100 text-primary-600 font-semibold text-xl">
              {company.name.charAt(0)}
            </div>
          )}
        </div>

        {/* Company Info */}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-semibold text-neutral-900 truncate">
                {company.name}
                {company.featured && (
                  <span className="ml-2 badge badge-accent">แนะนำ</span>
                )}
              </h3>

              {company.location && (
                <p className="text-sm text-neutral-600 mt-1">
                  📍 {company.location}
                </p>
              )}
            </div>
          </div>

          {/* Description */}
          {showDescription && company.description && (
            <p className="text-sm text-neutral-700 mt-2 line-clamp-2">
              {company.description}
            </p>
          )}

          {/* Services */}
          {showServices && company.services?.length && (
            <div className="flex flex-wrap gap-1 mt-3">
              {company.services.slice(0, 3).map((service) => (
                <span key={service} className="badge badge-primary">
                  {service}
                </span>
              ))}
              {company.services.length > 3 && (
                <span className="badge badge-neutral">
                  +{company.services.length - 3} อื่นๆ
                </span>
              )}
            </div>
          )}

          {/* Contact Actions */}
          <div className="flex items-center space-x-2 mt-4">
            {company.phone && (
              <button
                onClick={() => onContact?.('phone', company.phone!)}
                className="contact-btn"
              >
                📞 โทร
              </button>
            )}
            {company.website && (
              <button
                onClick={() => onContact?.('website', company.website!)}
                className="contact-btn bg-secondary-500 hover:bg-secondary-600"
              >
                🌐 เว็บไซต์
              </button>
            )}
          </div>
        </div>
      </div>
    </Card>
  )
)

CompanyCard.displayName = 'CompanyCard'

export {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  CardImage,
  CompanyCard,
  cardVariants
}